/**
 * Modal for secretaris/beheerder to send a werkgroep-ledenlijst intake invitation.
 * Can be opened from GroupDetailPage (pre-selected group) or standalone.
 */
import { useState, useEffect } from "react";
import { WERKGROEP_INTAKE_LINK_VALIDITY_DAYS } from "@domaddress/shared";
import { api } from "../lib/api";

type Group = { id: number; name: string };

const DEFAULT_EMAIL_BODY = `Beste [[VOORZITTER_FIRSTNAME]],

Via de onderstaande link kunt u de leden van [[GROEPNAAM]] doorgeven of bijwerken. Klik op de link om het formulier te openen:

[[WERKGROEP_LINK]]

De link is ${WERKGROEP_INTAKE_LINK_VALIDITY_DAYS} dagen geldig. Na ontvangst worden de gegevens door de secretaris verwerkt.

Met vriendelijke groet,
De secretaris van Dominicus Amsterdam`;

const PLACEHOLDER_HINTS: { label: string; value: string }[] = [
  { label: "Werkgroep-link (verplicht!)", value: "[[WERKGROEP_LINK]]" },
  { label: "Groepnaam", value: "[[GROEPNAAM]]" },
  { label: "Voorzitter voornaam", value: "[[VOORZITTER_FIRSTNAME]]" },
];

export type SendWerkgroepIntakeModalProps = {
  /** If provided, this group is pre-selected and the group picker is hidden. */
  preselectedGroup?: Group;
  onClose: () => void;
  onSent?: () => void;
};

export default function SendWerkgroepIntakeModal({
  preselectedGroup,
  onClose,
  onSent,
}: SendWerkgroepIntakeModalProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(!preselectedGroup);
  const [selectedGroupId, setSelectedGroupId] = useState<number | "">(
    preselectedGroup?.id ?? ""
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);
  const [sendState, setSendState] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [chairLoading, setChairLoading] = useState(false);

  // Load groups if no preselected group
  useEffect(() => {
    if (preselectedGroup) return;
    void (async () => {
      try {
        const res = await api.get<{ data: Group[] }>("/api/groups");
        setGroups(
          (res.data.data ?? [])
            .map((g) => ({ id: g.id, name: g.name }))
            .sort((a, b) => a.name.localeCompare(b.name, "nl"))
        );
      } catch {
        // ignore
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, [preselectedGroup]);

  // When a group is (pre)selected, fetch its voorzitter and pre-fill recipient e-mail
  useEffect(() => {
    const gid = preselectedGroup?.id ?? (typeof selectedGroupId === "number" ? selectedGroupId : null);
    if (!gid) return;
    setChairLoading(true);
    void (async () => {
      try {
        const res = await api.get<{
          data: {
            view_mode?: string;
            chair?: { email?: string | null } | null;
            chair_contact?: { email?: string | null } | null;
          };
        }>(`/api/groups/${gid}`);
        const g = res.data.data;
        // chair_contact.email (from medewerkerkaart) takes priority over chair user email
        const email =
          g?.chair_contact?.email?.trim() ||
          g?.chair?.email?.trim() ||
          "";
        if (email) setRecipientEmail(email);
      } catch {
        // not critical — secretary can type it manually
      } finally {
        setChairLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedGroup?.id, selectedGroupId]);

  function insertPlaceholder(value: string) {
    setEmailBody((prev) => {
      const textarea = document.getElementById("intake-email-body") as HTMLTextAreaElement | null;
      if (textarea) {
        const start = textarea.selectionStart ?? prev.length;
        const end = textarea.selectionEnd ?? prev.length;
        return prev.slice(0, start) + value + prev.slice(end);
      }
      return prev + "\n" + value;
    });
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedGroupId) {
      setErrorMsg("Selecteer een groep.");
      return;
    }
    if (!recipientEmail.trim()) {
      setErrorMsg("Vul een e-mailadres in.");
      return;
    }
    if (!emailBody.includes("[[WERKGROEP_LINK]]")) {
      setErrorMsg("De e-mailtekst moet de placeholder [[WERKGROEP_LINK]] bevatten zodat de voorzitter op de link kan klikken.");
      return;
    }
    setSendState("sending");
    setErrorMsg("");
    try {
      await api.post("/api/werkgroep-intakes/send-invite", {
        group_id: Number(selectedGroupId),
        recipient_email: recipientEmail.trim(),
        email_body: emailBody,
      });
      setSendState("success");
      onSent?.();
    } catch (err: unknown) {
      setSendState("error");
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Er is een fout opgetreden bij het versturen.";
      setErrorMsg(msg);
    }
  }

  const selectedGroupName = preselectedGroup?.name ?? groups.find((g) => g.id === selectedGroupId)?.name ?? "";

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ maxWidth: 580, width: "100%" }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ margin: 0 }}>
            Uitnodiging ledenlijst sturen
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </div>

        {sendState === "success" ? (
          <div className="modal-body">
            <p style={{ color: "#1a6e35", background: "#eafaf1", border: "1px solid #b7e0c4", borderRadius: 6, padding: "0.75rem 1rem" }}>
              ✓ Uitnodiging verstuurd naar <strong>{recipientEmail}</strong> voor{" "}
              <strong>{selectedGroupName}</strong>. De voorzitter heeft {WERKGROEP_INTAKE_LINK_VALIDITY_DAYS} dagen om
              het formulier in te vullen.
            </p>
            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn-primary" onClick={onClose}>
                Sluiten
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { void handleSend(e); }}>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {/* Group picker */}
              {preselectedGroup ? (
                <div>
                  <label className="form-label">Groep</label>
                  <p className="form-input" style={{ margin: 0, background: "#f5f3f8", color: "#4a4260" }}>
                    {preselectedGroup.name}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="form-label" htmlFor="intake-group-select">
                    Groep *
                  </label>
                  {groupsLoading ? (
                    <div className="loading-spinner" style={{ width: 24, height: 24 }} />
                  ) : (
                    <select
                      id="intake-group-select"
                      className="form-input"
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value ? Number(e.target.value) : "")}
                      required
                    >
                      <option value="">— Kies een groep —</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Recipient email */}
              <div>
                <label className="form-label" htmlFor="intake-recipient">
                  E-mailadres ontvanger *
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    id="intake-recipient"
                    type="email"
                    className="form-input"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    required
                    maxLength={254}
                    placeholder="voorzitter@werkgroep.nl"
                    style={{ flex: 1 }}
                  />
                  {chairLoading && (
                    <span style={{ fontSize: "0.78rem", color: "#7c6e90", whiteSpace: "nowrap" }}>
                      laden…
                    </span>
                  )}
                </div>
                <p style={{ fontSize: "0.8rem", color: "#7c6e90", marginTop: 4 }}>
                  Automatisch ingevuld vanuit het voorzitter-profiel van de groep. U kunt het aanpassen.
                </p>
              </div>

              {/* Email body */}
              <div>
                <label className="form-label" htmlFor="intake-email-body">
                  E-mailtekst
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.4rem" }}>
                  {PLACEHOLDER_HINTS.map((ph) => (
                    <button
                      key={ph.value}
                      type="button"
                      onClick={() => insertPlaceholder(ph.value)}
                      style={{
                        fontSize: "0.75rem",
                        background: "#f0ebf8",
                        border: "1px solid #c8bde0",
                        borderRadius: 4,
                        padding: "0.2rem 0.5rem",
                        cursor: "pointer",
                        color: "#4a3070",
                      }}
                      title={`Voeg ${ph.value} in op cursor-positie`}
                    >
                      + {ph.label}
                    </button>
                  ))}
                </div>
                <textarea
                  id="intake-email-body"
                  className="form-input"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={12}
                  maxLength={4000}
                  style={{
                    fontFamily: "inherit",
                    fontSize: "0.9rem",
                    resize: "vertical",
                    width: "100%",
                    boxSizing: "border-box",
                    display: "block",
                  }}
                />
                <p style={{ fontSize: "0.78rem", color: "#7c6e90", marginTop: 4 }}>
                  De placeholders <code>[[WERKGROEP_LINK]]</code>, <code>[[GROEPNAAM]]</code> en{" "}
                  <code>[[VOORZITTER_FIRSTNAME]]</code> worden automatisch vervangen voor verzending.
                  <code>[[WERKGROEP_LINK]]</code> is verplicht.
                </p>
              </div>

              {errorMsg && (
                <p
                  style={{
                    color: "#c0392b",
                    background: "#fff0f0",
                    border: "1px solid #f5c2c7",
                    borderRadius: 6,
                    padding: "0.6rem 0.9rem",
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  {errorMsg}
                </p>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={sendState === "sending"}>
                Annuleren
              </button>
              <button type="submit" className="btn-primary" disabled={sendState === "sending"}>
                {sendState === "sending" ? "Versturen…" : "Uitnodiging versturen"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
